import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Typography,
  Chip,
  Stack,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import axios from 'axios';
import { format, addYears, differenceInDays } from 'date-fns';
import { ar } from 'date-fns/locale';

interface DocumentAlert {
  _id: string;
  documentName: string;
  documentNumber: string;
  expiryDate: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const DocumentAlertsPage: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentAlert[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openRenewDialog, setOpenRenewDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentAlert | null>(null);
  const [renewYears, setRenewYears] = useState<number>(1);
  const [customDate, setCustomDate] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    documentName: '',
    documentNumber: '',
    expiryDate: '',
    notes: ''
  });

  // Fetch documents
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/settings/document-alerts', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search
        }
      });
      setDocuments(response.data.documents);
      setTotal(response.data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطأ في جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [page, rowsPerPage, search]);

  // Handle form submit
  const handleSubmit = async () => {
    try {
      if (selectedDocument) {
        await axios.put(`/api/settings/document-alerts/${selectedDocument._id}`, formData);
        setSuccess('تم تحديث المستند بنجاح');
      } else {
        await axios.post('/api/settings/document-alerts', formData);
        setSuccess('تم إضافة المستند بنجاح');
      }
      setOpenDialog(false);
      resetForm();
      fetchDocuments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطأ في حفظ البيانات');
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستند؟')) return;
    
    try {
      await axios.delete(`/api/settings/document-alerts/${id}`);
      setSuccess('تم حذف المستند بنجاح');
      fetchDocuments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطأ في حذف المستند');
    }
  };

  // Handle renew
  const handleRenew = async () => {
    if (!selectedDocument) return;
    
    try {
      const payload: any = {};
      if (customDate) {
        payload.newExpiryDate = customDate;
      } else {
        payload.years = renewYears;
      }
      
      await axios.post(`/api/settings/document-alerts/${selectedDocument._id}/renew`, payload);
      setSuccess('تم تجديد المستند بنجاح');
      setOpenRenewDialog(false);
      setSelectedDocument(null);
      setCustomDate('');
      setRenewYears(1);
      fetchDocuments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطأ في تجديد المستند');
    }
  };

  // Open dialog for add/edit
  const openFormDialog = (document?: DocumentAlert) => {
    if (document) {
      setSelectedDocument(document);
      setFormData({
        documentName: document.documentName,
        documentNumber: document.documentNumber,
        expiryDate: format(new Date(document.expiryDate), 'yyyy-MM-dd'),
        notes: document.notes || ''
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  // Open renew dialog
  const openRenewFormDialog = (document: DocumentAlert) => {
    setSelectedDocument(document);
    const newDate = addYears(new Date(document.expiryDate), 1);
    setCustomDate(format(newDate, 'yyyy-MM-dd'));
    setOpenRenewDialog(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      documentName: '',
      documentNumber: '',
      expiryDate: '',
      notes: ''
    });
    setSelectedDocument(null);
  };

  // Get status color based on expiry date
  const getStatusColor = (expiryDate: string) => {
    const days = differenceInDays(new Date(expiryDate), new Date());
    if (days < 0) return 'error';
    if (days <= 30) return 'warning';
    if (days <= 90) return 'info';
    return 'success';
  };

  // Get status text
  const getStatusText = (expiryDate: string) => {
    const days = differenceInDays(new Date(expiryDate), new Date());
    if (days < 0) return 'منتهي';
    if (days === 0) return 'ينتهي اليوم';
    if (days <= 30) return `${days} يوم متبقي`;
    if (days <= 90) return `${days} يوم متبقي`;
    return `${days} يوم متبقي`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          التنبيهات
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openFormDialog()}
        >
          إضافة مستند
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search and Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="البحث في المستندات..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <IconButton onClick={fetchDocuments} color="primary">
              <RefreshIcon />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>اسم المستند</TableCell>
                <TableCell>رقم المعرف</TableCell>
                <TableCell>تاريخ الانتهاء</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>الملاحظات</TableCell>
                <TableCell align="center">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    جاري التحميل...
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    لا توجد مستندات
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow key={doc._id}>
                    <TableCell>{doc.documentName}</TableCell>
                    <TableCell>{doc.documentNumber}</TableCell>
                    <TableCell>
                      {format(new Date(doc.expiryDate), 'dd/MM/yyyy', { locale: ar })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(doc.expiryDate)}
                        color={getStatusColor(doc.expiryDate)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{doc.notes || '-'}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openRenewFormDialog(doc)}
                        >
                          تجديد
                        </Button>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openFormDialog(doc)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(doc._id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="عدد الصفوف:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
        />
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedDocument ? 'تعديل المستند' : 'إضافة مستند جديد'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="اسم المستند"
              value={formData.documentName}
              onChange={(e) => setFormData({ ...formData, documentName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="رقم المعرف"
              value={formData.documentNumber}
              onChange={(e) => setFormData({ ...formData, documentNumber: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="تاريخ الانتهاء"
              type="date"
              value={formData.expiryDate}
              onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="الملاحظات"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>إلغاء</Button>
          <Button onClick={handleSubmit} variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Renew Dialog */}
      <Dialog open={openRenewDialog} onClose={() => setOpenRenewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>تجديد المستند</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Alert severity="info">
              التاريخ الحالي: {selectedDocument && format(new Date(selectedDocument.expiryDate), 'dd/MM/yyyy', { locale: ar })}
            </Alert>
            
            <FormControl fullWidth>
              <InputLabel>تجديد سريع</InputLabel>
              <Select
                value={renewYears}
                onChange={(e) => {
                  const years = Number(e.target.value);
                  setRenewYears(years);
                  if (selectedDocument) {
                    const newDate = addYears(new Date(selectedDocument.expiryDate), years);
                    setCustomDate(format(newDate, 'yyyy-MM-dd'));
                  }
                }}
                label="تجديد سريع"
              >
                <MenuItem value={1}>سنة واحدة</MenuItem>
                <MenuItem value={2}>سنتين</MenuItem>
                <MenuItem value={3}>ثلاث سنوات</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="أو اختر تاريخ مخصص"
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <Alert severity="success">
              التاريخ الجديد: {customDate && format(new Date(customDate), 'dd/MM/yyyy', { locale: ar })}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRenewDialog(false)}>إلغاء</Button>
          <Button onClick={handleRenew} variant="contained" color="primary">
            تأكيد التجديد
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentAlertsPage;